import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyMoneyMindUsers } from './companyMoneyMindApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the MoneyMind section.
 *
 * "Entries" and "Days Logged" sit side by side because they answer different
 * questions: forty entries across two days is one long sitting, forty across
 * twenty days is a habit.
 *
 * There is no amount column, and there is no way to add one from this endpoint —
 * the API never returns a price. Every user of the population is listed,
 * including those who have never logged anything; they show dashes rather than
 * zeros.
 */
const MoneyMindUsersTable = ({
  fetcher = fetchCompanyMoneyMindUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const hasEntries = row.entries > 0;
          return {
            ...row,
            entriesText: hasEntries ? row.entries.toLocaleString() : '-',
            daysText: hasEntries ? row.activeDays.toLocaleString() : '-',
            letGoText: hasEntries ? row.letGo.toLocaleString() : '-',
            lastEntryDate: row.lastEntryDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('MoneyMindUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Entries', key: 'entriesText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Days Logged', key: 'daysText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Let It Go', key: 'letGoText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Last Entry',
      key: 'lastEntryDate',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

MoneyMindUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default MoneyMindUsersTable;
